import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Search,
  DropdownList,
  LabelWithInput,
  ButtonSearch,
} from "../utils/Common";
import { MdKeyboardArrowDown } from "react-icons/md";
import { IoIosCloseCircleOutline } from "react-icons/io";
import useOnClickOutside from "./useOnClickOutside";
import styled from "styled-components";

const Option = styled.span`
  padding: 0.5rem;
  border: 1px solid lightgrey;
  width: 100%;
  :hover {
    background-color: lightblue;
  }
`;

function useSearch<T extends string>(
  _dropdownList: T[],
  onSearch: (searchType: T, searchText: string) => void,
  onClear: () => void,
  placeholders: Record<T, string>,
  options?: Record<T, string[]>
) {
  const [dropdownList, setDropdownList] = useState(_dropdownList);
  const [searchType, setSearchType] = useState(_dropdownList[0]);
  const [value, setValue] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(optionsRef, () => setShowOptions(false));

  const isFirstRender = useRef(true);

  const keyListener = useCallback(
    (e: KeyboardEvent) => {
      if (
        e.key === "Enter" &&
        ref.current &&
        ref.current.contains(e.target as Node)
      )
        onSearch(searchType, value);
    },
    [searchType, value]
  );

  useEffect(() => {
    document.addEventListener("keyup", keyListener);
    return () => {
      document.removeEventListener("keyup", keyListener);
    };
  }, [keyListener]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (value === "") onClear();
  }, [value]);

  const searchQuery = useMemo(() => {
    if (!value) return "";
    let query = "&";
    switch (searchType) {
      case "Name":
        query = query + "name";
        break;
      case "Customer ID":
        query = query + "customerID";
        break;
      case "Mobile":
        query = query + "mobile";
        break;
      case "PAN":
        query = query + "pan";
        break;
      case "Email":
        query = query + "email";
        break;
      case "Agent Code":
        query = query + "partnerCode";
        break;
      case "Agent Name":
        query = query + "partnerName";
        break;
      case "Loan Application ID":
        query = query + "loanApplicationNum";
        break;
      case "Firm Name":
        query = query + "firmName";
        break;
      case "Queue":
        query = query + "tag";
        break;
      case "FinBox Txn ID":
        query = query + "txnID";
        break;
      case "UTR":
        query = query + "utrNo";
        break;
      case "Partner Txn ID":
        query = query + "partnerTxnID";
        break;
      case "Loan ID":
        query = query + "loanID";
        break;
      case "Bill ID":
        query = query + "billID";
        break;
      case "Source":
        query = query + "source";
        break;
      case "Invoice No":
        query = query + "invoiceNo";
    }
    return query + "=" + value;
  }, [searchType, value]);

  let filteredOptions: string[];

  if (options && options[searchType])
    filteredOptions = options[searchType].filter((tag) =>
      tag.toLowerCase().includes(value.substring(0, value.length).toLowerCase())
    );

  const SearchComponent = () => {
    return (
      <Search style={{ marginRight: "25px" }}>
        <DropdownList className="dropdown" style={{ display: "flex" }}>
          <div
            className="dropdown-toggle"
            tabIndex={0}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "4px 12px",
              cursor: "pointer",
              boxShadow: "none",
              border: "1px solid #ececec",
              borderRadius: "2px",
              color: "#3e3e3e",
              width: "220px",
              height: "34.8px",
              borderRight: "none",
              background: "aliceblue",
            }}
          >
            <span>{searchType}</span>
            <span
              style={{
                marginLeft: 5,
                color: "#c4c4c4",
                display: "flex",
                alignItems: "center",
              }}
            >
              <MdKeyboardArrowDown />
            </span>
          </div>
          <ul className="menu niceScroll" style={{ maxWidth: "220px" }}>
            {dropdownList.map((item) => (
              <li
                key={item}
                onClick={() => {
                  setSearchType(item);
                  setValue("");
                }}
              >
                {item}
              </li>
            ))}
          </ul>
        </DropdownList>
        <div ref={ref} style={{ position: "relative" }}>
          <LabelWithInput
            name="Search"
            value={value}
            placeholder={
              placeholders?.[searchType]
                ? placeholders[searchType]
                : `SearchInput by ${searchType.toLowerCase()}`
            }
            search={true}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            style={{ flexGrow: "1", width: "240px" }}
          />
          {options && options[searchType] && showOptions && (
            <div
              ref={optionsRef}
              onClick={(e) => {
                if (!(e.target instanceof HTMLSpanElement)) return;
                const tagName = e.target.dataset.tagName;
                if (!tagName) return;
                setValue(tagName);
                setShowOptions(false);
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                position: "absolute",
                top: "100%",
                background: "white",
                border: "1px solid grey",
                borderRadius: "0 0 4px 4px",
                width: "100%",
                left: 0,
                right: 0,
              }}
            >
              {filteredOptions.map((tag) => (
                <Option data-tag-name={tag}>{tag}</Option>
              ))}
              {filteredOptions && filteredOptions.length === 0 && (
                <Option>No tag found</Option>
              )}
            </div>
          )}
          <IoIosCloseCircleOutline
            onClick={() => {
              setValue("");
            }}
            style={{
              position: "absolute",
              right: "10px",
              top: "10px",
              cursor: "pointer",
              display: value === "" ? "none" : "block",
            }}
          />
        </div>

        <ButtonSearch
          style={{
            marginRight: value === "" ? "16px" : "0",
          }}
          onClick={() => onSearch(searchType, value)}
        >
          <img
            src="https://cdn-ind-01.finbox.in/assets/dashboard/search.svg"
            id="search"
          />
        </ButtonSearch>
      </Search>
    );
  };
  return {
    SearchComponent,
    searchType,
    searchValue: value,
    searchQuery,
    setDropdownList,
  };
}

export default useSearch;
